import { Injectable } from '@angular/core';
import { AccountInfo } from '@azure/msal-browser';
import { GraphService } from './graph.service';
import { environment } from '../../environments/environment';
import { ConnectorOption } from '../models/batch.interface';

@Injectable({ providedIn: 'root' })
export class AuthorizationService {
  constructor(private graphService: GraphService) {}

  private getConfiguredGroupIds(): {
    adminGroupId?: string;
    connectorGroupMap: Record<string, string[]>;
    connectorGroups: string[];
    allGroupIds: string[];
  } {
    const env = environment as unknown as {
      connectorGroupMap?: Record<string, string[]>;
      adminGroupId?: string;
    };
    const connectorGroupMap: Record<string, string[]> =
      env.connectorGroupMap || {};
    const adminGroupId = env.adminGroupId;
    const connectorGroups = Object.values(connectorGroupMap).flat().filter(Boolean);
    const allGroupIds = Array.from(
      new Set([...(adminGroupId ? [adminGroupId] : []), ...connectorGroups])
    );

    return { adminGroupId, connectorGroupMap, connectorGroups, allGroupIds };
  }

  /**
   * Determine admin + authorization status for an account.
    * Uses Microsoft Graph group membership as the sole source of truth.
    * Returns `isAuthorized` when the user is admin or belongs to any configured connector group.
   */
  public async evaluateAuthorization(
    account: AccountInfo
  ): Promise<{ isAdmin: boolean; isAuthorized: boolean }> {
    const claims = account.idTokenClaims as Record<string, unknown> | undefined;
    const userId = claims?.['oid'] as string | undefined;
    const { adminGroupId, connectorGroups, allGroupIds } = this.getConfiguredGroupIds();

    if (!userId || allGroupIds.length === 0) {
      return { isAdmin: false, isAuthorized: false };
    }

    try {
      const matched = await this.graphService.checkMemberGroups(userId, allGroupIds);
      const isAdmin = Boolean(adminGroupId && matched.includes(adminGroupId));
      const isAuthorized = isAdmin || connectorGroups.some((g) => matched.includes(g));
      return { isAdmin, isAuthorized };
    } catch (e) {
      console.warn('[AuthorizationService] Graph membership check failed', e);
      throw e;
    }
  }

  /**
   * Compute available connector options for the account.
    * Uses Microsoft Graph group membership as the sole source of truth.
    * Returns `allOptions` for admins; otherwise filters by configured connector group membership.
   */
  public async getAvailableConnectors(
    account: AccountInfo | null,
    allOptions: ConnectorOption[]
  ): Promise<ConnectorOption[]> {
    if (!account) return [];

    const claims = account.idTokenClaims as Record<string, unknown> | undefined;
    const userId = claims?.['oid'] as string | undefined;
    const { adminGroupId, connectorGroupMap, allGroupIds } = this.getConfiguredGroupIds();

    if (!userId || allGroupIds.length === 0) {
      return [];
    }

    try {
      const matched = await this.graphService.checkMemberGroups(userId, allGroupIds);
      if (adminGroupId && matched.includes(adminGroupId)) {
        return allOptions;
      }

      return allOptions.filter((p) => {
        const allowedGroupsForConnector = connectorGroupMap?.[p.value] || [];
        return allowedGroupsForConnector.some((g) => matched.includes(g));
      });
    } catch (e) {
      console.warn('[AuthorizationService] Graph fallback failed', e);
      return [];
    }
  }
}
